import axios from "axios";
import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";
import styled from "styled-components/macro";
import useSWR from "swr";
import { Tab } from "../../../../../components/ReachTabs/Tab";
import { TabList } from "../../../../../components/ReachTabs/TabList";
import { TabPanel, TabPanels, Tabs } from "@reach/tabs";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../components/Error";
import {
  CalendarIcon,
  CardCompletionBadgeIcon,
  CompanyIcon,
  TemplatesNavIcon,
} from "../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import {
  getProductStatusColor,
  getProductStatusText,
  StatusLeft,
} from "../../../../../components/Status/Status";
import {
  ButtonWithConfirmDialog,
  PrimaryButtonMediumV2,
  DestructiveButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { ConfirmDialog } from "../../../../../components/ConfirmDialog/ConfirmDialog";
import { TextSmRegular } from "../../../../../components/Typography/TypographyV2";
import { PageWrapper } from "../../../../../layout/portalPageLayout";
import {
  PROFILE_DETAIL_CLASSES,
  ProfileDetailContentContainer,
} from "../../../../../layout/shared/ProfileDetailLayout";
import { FullwidthPageWrapper } from "../../../../../components/ProductsLayout/SharedProductsLayout";
import { endpoints } from "../../../../../endpoints";
import type { DataMutate, Permission } from "../../../../../types/types";
import type {
  EntityProfile,
  EntityStatus,
  EntityType,
} from "../../../../../types/types.MDM";
import {
  ENTITY_STATUS_LABELS,
  ENTITY_MODIFY_PERMISSIONS,
} from "../../../../../types/types.MDM";
import {
  isAxiosError,
  makeUrlWithParams,
  useStoreState,
} from "../../../../../util/util";
import { useLogProfileView } from "../../../../../util/activityTracking";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import {
  EntityGroupCard,
  entityAttributeHasDisplayValue,
} from "./EntityGroupCard";
import { EntityCollectionCard } from "./EntityCollectionCard";
import {
  collectionHasDisplayableValues,
  getEntityDetailEndpoint,
} from "./utils";
import { EntityShareButton } from "./EntityShareButton";
import { DetailPageHeader } from "../../../../../components/DetailPage";
import type {
  DetailPageHeaderConfig,
  MetadataItem,
} from "../../../../../components/DetailPage";
import { ActivityHistory } from "../../../../SharedPages/ProductDetailPage/Activity/ActivityHistory";
import { CompanyHierarchy } from "./CompanyHierarchy/CompanyHierarchy";
import { EntityDocuments } from "./EntityDocuments";
import {
  shouldShowEntityDocumentsTab,
  useDocumentAvailability,
} from "../../../../SharedPages/ProductDetailPage/hooks/useDocumentAvailability";
import { useAuthContext } from "../../../../../components/Auth";
import { useRoutePath } from "../../../../../util/Routing";
import {
  RelatedEntitiesTabContent,
  useRelatedEntities,
  getIsRelatedEntitiesReady,
  getShouldShowRelatedEntitiesTab,
} from "../../../../../components/ObjectReference/RelatedEntitiesTab";
import {
  checkObjectReferenceImpact,
  getEntityArchiveOrDeleteWarningMessage,
  requestObjectReferenceActionConfirmation,
} from "../../../../../components/ObjectReference/utils";
import { ObjectReferenceChangeConfirmDialog } from "../../../../../components/ObjectReference/ObjectReferenceChangeConfirmDialog";
import { CompletionScore } from "../../../../../components/CompletionScore/CompletionScore";
import { SubheaderContainer } from "../../../../../components/Layout/Layout";
import {
  AttributeGroupSummary,
  CollectionSchema,
  TemplateTabData,
} from "../../../../../types/types.PIM";
import ReactTooltip from "react-tooltip";
import { fontSizeTokens, spacingTokens } from "../../../../../designTokens";
import { ChangeRequestsListPage } from "../../../../../components/ChangeRequestForm/ChangeRequestTable";

const PaddedPrimaryButton = styled(PrimaryButtonMediumV2)`
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  white-space: nowrap;
  margin-bottom: 5px;
`;

const PaddedDestructiveButton = styled(DestructiveButtonMediumV2)`
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  white-space: nowrap;
  margin-bottom: 5px;
`;

const ShareOnlyActionSlot = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;

// Container for attribute groups in the overview tab
const GroupsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

function formatDate(dateValue?: string | null): string {
  if (!dateValue) {
    return "—";
  }
  return new Date(dateValue).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getGroupEndpoint(
  entityType: EntityType,
  tenantIdOrSlug: string,
  entityId: string,
  groupId: string
): string {
  switch (entityType) {
    case "customer":
      return endpoints.v2_tenants_id_customers_id_groups_id(
        tenantIdOrSlug,
        entityId,
        groupId
      );
    case "supplier":
      return endpoints.v2_tenants_id_suppliers_id_groups_id(
        tenantIdOrSlug,
        entityId,
        groupId
      );
    case "distributor":
      return endpoints.v2_tenants_id_distributors_id_groups_id(
        tenantIdOrSlug,
        entityId,
        groupId
      );
  }
}

type EntityDetailPageProps = {
  entityType: EntityType;
  listPath: string;
};
type PendingEntityAction = "archived" | "deleted";

/**
 * Build metadata configuration for entity header
 */
function buildEntityMetadata(
  entity: EntityProfile,
  _entityType: EntityType,
  t: (key: string) => string,
  listPath: string,
  getTemplateDisplayName: (
    template: {
      template_name?: string | null;
      template_type?: string | null;
    },
    fallback?: string
  ) => string
): MetadataItem[][] {
  const firstRow: MetadataItem[] = [];
  const secondRow: MetadataItem[] = [];

  if (entity.schema) {
    const adminPath = listPath.replace(/\/mdm\/.*$/, "");
    firstRow.push({
      id: "template",
      icon: TemplatesNavIcon,
      label: t("Template"),
      value: getTemplateDisplayName(entity.schema),
      link: `${adminPath}/pim/templates/${entity.schema.template_id}/groups`,
    });
  }
  firstRow.push({
    id: "modified",
    icon: CalendarIcon,
    label: t("Last Modified"),
    value: formatDate(entity.modified_at),
  });

  if (entity.parent_entity_name) {
    firstRow.push({
      id: "parent",
      label: t("Parent"),
      value: entity.parent_entity_name,
    });
  }
  if (entity.subsidiaries_count) {
    firstRow.push({
      id: "subsidiary",
      label: t("Subsidiaries"),
      value: entity.subsidiaries_count,
    });
  }

  // ─── Completion scores ──────────────────────────────────
  const showTotal = entity.schema?.show_total_completion_score;
  const showMandatory = entity.schema?.show_mandatory_completion_score;
  if (showTotal || showMandatory) {
    const score =
      entity.status === "staged" || entity.status === "unpublished_staged"
        ? entity.display_completion_score
        : entity.completion_score;

    const mandatoryScore =
      entity.status === "staged" || entity.status === "unpublished_staged"
        ? entity.display_mandatory_completion_score ??
          entity.mandatory_completion_score
        : entity.mandatory_completion_score;

    secondRow.push({
      id: "completion_score",
      icon: CardCompletionBadgeIcon,
      label: t("Completion Score"),
      value: (
        <SubheaderContainer style={{ marginTop: spacingTokens.spacing_xs }}>
          {showTotal && (
            <CompletionScore
              score={score}
              type="total"
              tooltipId="entity-total-completion"
              attributeCompletionValues={entity.attribute_completion_values}
            />
          )}
          {showTotal && showMandatory && (
            <span
              style={{
                lineHeight: "10px",
                fontSize: fontSizeTokens.font_size_xs,
              }}
            >
              |
            </span>
          )}
          {showMandatory && typeof mandatoryScore === "number" && (
            <CompletionScore
              score={mandatoryScore}
              type="mandatory"
              tooltipId="entity-mandatory-completion"
              attributeCompletionValues={entity.attribute_completion_values}
            />
          )}
        </SubheaderContainer>
      ),
    });
  }

  return [firstRow, secondRow];
}

function EntityTab({
  entity,
  mutate,
  entityType,
  tenantId,
  tab,
}: {
  entity: EntityProfile;
  mutate: DataMutate<EntityProfile>;
  entityType: EntityType;
  tenantId: string;
  tab: TemplateTabData;
}): JSX.Element {
  const { t } = useTranslation();
  const { hasPermission } = useAuthContext();
  const {
    storefront_metadata: { object_reference_enabled },
  } = useStoreState();
  const canModify = hasPermission(ENTITY_MODIFY_PERMISSIONS[entityType]);
  const hasGroups = entity.groups && entity.groups.length > 0;
  const hasCollections = entity.collections && entity.collections.length > 0;
  const shouldHideEmptyContent = !canModify;
  const showCompanyHierachy = entity.schema?.enable_company_hierarchy;

  if (!hasGroups && !hasCollections) {
    return (
      <TextSmRegular>
        {t("No attribute groups or collections on this template.")}
      </TextSmRegular>
    );
  }

  return (
    <GroupsContainer>
      {tab.items.map((item) => {
        if (
          item.type === "collection" &&
          !!entity.collections.find((collection) =>
            collection.name.toLowerCase().includes(item.name.toLowerCase())
          )
        ) {
          const collection = entity.collections.find(
            (collection) =>
              collection.name.toLowerCase() === item.name.toLowerCase()
          );
          const showCollection =
            collection?.is_visible &&
            collectionHasDisplayableValues(
              collection,
              object_reference_enabled,
              canModify
            );

          return (
            <Fragment key={item.id}>
              {!!collection && showCollection && (
                <EntityCollectionCard
                  key={item.id}
                  collection={collection}
                  entity={entity}
                  entityType={entityType}
                  tenantId={tenantId}
                  mutateEntity={mutate}
                  isEditable={canModify && entity.status !== "archived"}
                />
              )}
            </Fragment>
          );
        } else if (item.type === "group") {
          const group = entity.groups.find(
            (group) => group.name.toLowerCase() === item.name.toLowerCase()
          );

          const groupHasValues = () => {
            let hasValues = false;

            group?.attributes?.forEach((attribute) => {
              if (
                (entityAttributeHasDisplayValue(attribute) &&
                  shouldHideEmptyContent &&
                  !attribute.is_not_applicable &&
                  attribute.is_visible) ||
                (attribute.is_visible && !shouldHideEmptyContent)
              ) {
                hasValues = true;
              }
            });
            return hasValues;
          };
          const showGroup =
            (!!group?.attributes?.length && groupHasValues()) ||
            (canModify &&
              group?.attributes.some((attribute) => attribute.is_visible));
          return (
            <Fragment key={item.id}>
              {!!group && showGroup && (
                <EntityGroupCard
                  key={item.id}
                  entity={entity}
                  entityType={entityType}
                  group={group}
                  mutateEntity={mutate}
                  groupEndpoint={getGroupEndpoint(
                    entityType,
                    tenantId,
                    entity.id,
                    group.id
                  )}
                />
              )}
            </Fragment>
          );
        } else if (showCompanyHierachy && item.type === "company_hierarchy") {
          return (
            <Fragment key={item.id}>
              <CompanyHierarchyTab
                entity={entity}
                entityType={entityType}
                tenantId={tenantId}
                mutate={mutate}
                tab={tab}
              />
            </Fragment>
          );
        }
        return null;
      })}
    </GroupsContainer>
  );
}

export function ChangeRequestTab({
  entity,
  entityType,
  isTabActive,
  canModify,
}: {
  entity: EntityProfile;
  entityType: EntityType;
  canModify: boolean;
  isTabActive: boolean;
}): JSX.Element {
  return (
    <div className={PROFILE_DETAIL_CLASSES.detailsSection}>
      <ChangeRequestsListPage
        entityId={entity.id}
        entityType={entityType}
        isTabActive={isTabActive}
        canEdit={canModify}
      />
    </div>
  );
}

function CompanyHierarchyTab({
  entity,
  entityType,
  tenantId,
  mutate,
  tab,
}: {
  entity: EntityProfile;
  entityType: EntityType;
  tenantId: string;
  mutate: DataMutate<EntityProfile>;
  tab: TemplateTabData;
}): JSX.Element {
  return (
    <div className={PROFILE_DETAIL_CLASSES.detailsSection}>
      <CompanyHierarchy
        entity={entity}
        entityType={entityType}
        tenantId={tenantId}
        mutate={mutate}
        tab={tab}
      />
    </div>
  );
}

function ActivityTab({
  entity,
  entityType,
  isTabActive,
}: {
  entity: EntityProfile;
  entityType: EntityType;
  isTabActive: boolean;
}): JSX.Element {
  return (
    <ActivityHistory
      targetId={entity.id}
      targetType={entityType}
      isTabActive={isTabActive}
    />
  );
}

export function EntityDetailPage({
  entityType,
  listPath,
}: EntityDetailPageProps): JSX.Element {
  const { t } = useTranslation();
  const { getLabel, getTemplateDisplayName } = useMdmEntityDisplayNames();
  const entityLabelSingular = getLabel(entityType, "singular");
  const entityLabelPlural = getLabel(entityType, "plural");
  const { adminPath } = useRoutePath();
  const { entity_id } =
    useParams<{
      entity_id: string;
    }>();
  const { tenant_id, storefront_metadata: metadata } = useStoreState();
  const pageContainerSettings =
    metadata.theme_object?.designTokens?.productDetail?.container_settings
      ?.value || "no_containers";
  const surfaceBackground =
    metadata.theme_object?.designTokens?.productDetail?.color_bg_surface_product
      ?.value ?? "";
  const history = useHistory();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const [tabIndex, setTabIndex] = useState(0);
  const [loadingState, setLoadingState] = useState<string | undefined>();
  const [showArchiveWarningDialog, setShowArchiveWarningDialog] =
    useState(false);
  const [showArchiveBasicConfirmDialog, setShowArchiveBasicConfirmDialog] =
    useState(false);
  const [pendingEntityAction, setPendingEntityAction] =
    useState<PendingEntityAction | null>(null);
  const [{ changeRequestId }, setQuery] = useQueryParams({
    changeRequestId: StringParam,
    tabKey: StringParam,
  });

  const { hasPermission } = useAuthContext();
  const canModify = hasPermission(ENTITY_MODIFY_PERMISSIONS[entityType]);

  const modifyPermission = `modify_${entityType}s` as Permission;
  const publishPermission = `publish_${entityType}s` as Permission;
  const unpublishPermission = `unpublish_${entityType}s` as Permission;
  const deletePermission = `delete_${entityType}s` as Permission;

  const hasModifyOrPublishPermission =
    hasPermission(modifyPermission) || hasPermission(publishPermission);

  const detailEndpoint = makeUrlWithParams(
    getEntityDetailEndpoint(entityType, tenant_id, entity_id),
    {
      return_staged: hasModifyOrPublishPermission ? "true" : "false",
    }
  );

  const {
    data: entity,
    error,
    mutate,
  } = useSWR<EntityProfile>(detailEndpoint, {
    revalidateOnFocus: false,
  });
  // AP-1166: one profile view per company profile opened
  useLogProfileView(entity ? { type: entityType, id: entity.id } : undefined);
  const { data: relatedEntities, error: relatedEntitiesError } =
    useRelatedEntities(
      tenant_id,
      entityType,
      entity_id,
      Boolean(metadata?.object_reference_enabled)
    );

  const { hasAssetsOrExternalLinks, isLoading: isLoadingDocumentAvailability } =
    useDocumentAvailability(entityType, entity?.id);

  const showDocumentsTab = shouldShowEntityDocumentsTab({
    canModify,
    hasAssetsOrExternalLinks,
    isLoading: isLoadingDocumentAvailability,
  });
  const changeRequestTabIndexRef = useRef(-1);

  useEffect(() => {
    if (changeRequestId && changeRequestTabIndexRef.current !== -1) {
      setTabIndex(changeRequestTabIndexRef.current);
    }
  }, [changeRequestId, entity, relatedEntities, isLoadingDocumentAvailability]);

  const hasHierarchyImpact =
    Boolean(entity?.is_subsidiary) ||
    Boolean((entity?.subsidiaries_count ?? 0) > 0);

  const handleStatusChange = async (
    newStatus: string,
    options?: {
      skipArchiveConfirmation?: boolean;
    }
  ): Promise<boolean> => {
    setLoadingState(newStatus);
    try {
      if (newStatus === "archived" && !options?.skipArchiveConfirmation) {
        if (!metadata?.object_reference_enabled) {
          setPendingEntityAction("archived");
          if (hasHierarchyImpact) {
            setShowArchiveWarningDialog(true);
            return false;
          }
          setShowArchiveBasicConfirmDialog(true);
          return false;
        }

        const needsConfirmation =
          await requestObjectReferenceActionConfirmation({
            action: "archived",
            objectReferenceEnabled: true,
            shouldCheckImpact: true,
            hasHierarchyImpact,
            checkImpact: () =>
              checkObjectReferenceImpact({
                tenantIdOrSlug: tenant_id,
                objectType: entityType,
                objectId: entity_id,
              }),
            setPendingAction: setPendingEntityAction,
            showWarningDialog: () => setShowArchiveWarningDialog(true),
            showBasicConfirmDialog: () =>
              setShowArchiveBasicConfirmDialog(true),
          });
        if (needsConfirmation) {
          return false;
        }
      }

      await axios.patch<EntityProfile>(detailEndpoint, { status: newStatus });
      await mutate();
      notifySuccess(
        t("{{label}} status updated to {{status}}", {
          label: entityLabelSingular,
          status: t(
            ENTITY_STATUS_LABELS[newStatus as EntityStatus] ?? newStatus
          ),
        })
      );
      setLoadingState(undefined);
      return true;
    } catch (error) {
      if (isAxiosError(error)) {
        notifyError(t("There was an error updating the status"), { error });
      }
      setLoadingState(undefined);
      return false;
    }
  };

  const handleDelete = async (options?: {
    skipDeleteConfirmation?: boolean;
  }): Promise<boolean> => {
    setLoadingState("deleting");
    try {
      if (
        !options?.skipDeleteConfirmation &&
        metadata?.object_reference_enabled
      ) {
        const needsConfirmation =
          await requestObjectReferenceActionConfirmation({
            action: "deleted",
            objectReferenceEnabled: true,
            shouldCheckImpact: true,
            checkImpact: () =>
              checkObjectReferenceImpact({
                tenantIdOrSlug: tenant_id,
                objectType: entityType,
                objectId: entity_id,
              }),
            setPendingAction: setPendingEntityAction,
            showWarningDialog: () => setShowArchiveWarningDialog(true),
            showBasicConfirmDialog: () =>
              setShowArchiveBasicConfirmDialog(true),
          });
        if (needsConfirmation) {
          return false;
        }
      }

      await axios.delete(detailEndpoint);
      notifySuccess(t("{{label}} deleted", { label: entityLabelSingular }));
      history.push(listPath);
      return true;
    } catch (error) {
      if (isAxiosError(error)) {
        notifyError(t("There was an error deleting the entity"), { error });
      }
      setLoadingState(undefined);
      return false;
    }
  };

  const handleArchiveOrDeleteDialogClose = () => {
    setPendingEntityAction(null);
    setLoadingState(undefined);
  };

  const isDisabledBasedOnScoreThreshold =
    entity?.mandatory_completion_score !== null &&
    entity?.schema.show_mandatory_completion_score &&
    entity?.mandatory_completion_score <
      (metadata?.[`${entityType}_publish_completion_score_threshold`] ?? 0);

  const publishButtonDisabledMessage = isDisabledBasedOnScoreThreshold
    ? t(
        "Mandatory Completion Score must be at least {{threshold}}% to publish",
        {
          threshold:
            metadata?.[`${entityType}_publish_completion_score_threshold`],
        }
      )
    : undefined;

  const statusActionButtons = (): React.ReactNode => {
    const isLoading = Boolean(loadingState);
    const statusIndicator = (
      <StatusLeft
        color={getProductStatusColor(entity?.status ?? "draft")}
        text={getProductStatusText(entity?.status ?? "draft", t)}
        textStyle={{ fontSize: "13px" }}
        productStatusType="product"
      />
    );

    const shareButton = entity ? (
      <EntityShareButton
        entityId={entity.id}
        entityType={entityType}
        name={entity.name}
      />
    ) : null;

    // Users without modify/publish permissions see only the share button,
    // pinned to the right edge (same position as the action buttons).
    if (!hasModifyOrPublishPermission) {
      return <ShareOnlyActionSlot>{shareButton}</ShareOnlyActionSlot>;
    }

    switch (entity?.status) {
      case "draft":
        return (
          <>
            {statusIndicator}
            {shareButton}
            {metadata?.object_reference_enabled ? (
              <PaddedDestructiveButton
                onClick={() => void handleDelete()}
                loading={loadingState === "deleting"}
                disabled={isLoading || !hasPermission(deletePermission)}
                data-tip={
                  !hasPermission(deletePermission)
                    ? t("You do not have permission to delete this entity")
                    : undefined
                }
                data-for={
                  !hasPermission(deletePermission)
                    ? "delete_tooltip"
                    : undefined
                }
              >
                {t("Delete")}
              </PaddedDestructiveButton>
            ) : (
              <ButtonWithConfirmDialog
                Button={PaddedDestructiveButton}
                testid="draft_delete"
                confirmMessage={t(
                  "This will permanently delete this draft. Are you sure?"
                )}
                handleConfirm={() => void handleDelete()}
                loading={loadingState === "deleting"}
                disabled={isLoading || !hasPermission(deletePermission)}
                datatip={
                  !hasPermission(deletePermission)
                    ? t("You do not have permission to delete this entity")
                    : undefined
                }
                datafor={
                  !hasPermission(deletePermission)
                    ? "delete_tooltip"
                    : undefined
                }
                buttonText={t("Delete")}
              />
            )}
            <PaddedPrimaryButton
              onClick={() => handleStatusChange("published")}
              loading={loadingState === "published"}
              disabled={
                isLoading ||
                !hasPermission(publishPermission) ||
                isDisabledBasedOnScoreThreshold
              }
              datatip={
                !hasPermission(publishPermission)
                  ? t("You do not have permission to publish this entity")
                  : publishButtonDisabledMessage
              }
              datafor={
                !hasPermission(publishPermission) ||
                isDisabledBasedOnScoreThreshold
                  ? "publish_tooltip"
                  : undefined
              }
            >
              {t("Publish")}
            </PaddedPrimaryButton>
          </>
        );
      case "staged":
      case "unpublished_staged":
        return (
          <>
            {statusIndicator}
            {shareButton}
            <ButtonWithConfirmDialog
              Button={PaddedDestructiveButton}
              testid="discard_confirm"
              confirmMessage={t(
                "You will lose all changes made since the last publish. Are you sure you want to continue?"
              )}
              handleConfirm={() => handleStatusChange("discard")}
              loading={loadingState === "discard"}
              disabled={isLoading || !hasPermission(publishPermission)}
              datatip={
                !hasPermission(publishPermission)
                  ? t("You do not have permission to discard this entity")
                  : undefined
              }
              datafor={
                !hasPermission(publishPermission)
                  ? "discard_tooltip"
                  : undefined
              }
              buttonText={t("Discard Changes")}
            />
            <PaddedPrimaryButton
              onClick={() => handleStatusChange("published")}
              loading={loadingState === "published"}
              disabled={
                isLoading ||
                isDisabledBasedOnScoreThreshold ||
                !hasPermission(publishPermission)
              }
              datatip={
                !hasPermission(publishPermission)
                  ? t("You do not have permission to publish this entity")
                  : publishButtonDisabledMessage
              }
              datafor={
                !hasPermission(publishPermission) ||
                isDisabledBasedOnScoreThreshold
                  ? "publish_tooltip"
                  : undefined
              }
            >
              {t("Publish")}
            </PaddedPrimaryButton>
          </>
        );
      case "published":
        return (
          <>
            {statusIndicator}
            {shareButton}
            <PaddedPrimaryButton
              onClick={() => handleStatusChange("unpublished")}
              loading={loadingState === "unpublished"}
              disabled={isLoading || !hasPermission(unpublishPermission)}
              datatip={
                !hasPermission(unpublishPermission)
                  ? t("You do not have permission to unpublish this entity")
                  : undefined
              }
              datafor={
                !hasPermission(unpublishPermission)
                  ? "unpublish_tooltip"
                  : undefined
              }
            >
              {t("Unpublish")}
            </PaddedPrimaryButton>
          </>
        );
      case "unpublished":
        return (
          <>
            {statusIndicator}
            {shareButton}
            <PaddedDestructiveButton
              onClick={() => void handleStatusChange("archived")}
              loading={loadingState === "archived"}
              disabled={isLoading || !hasPermission(deletePermission)}
              datatip={
                !hasPermission(deletePermission)
                  ? t("You do not have permission to archive this entity")
                  : undefined
              }
              datafor={
                !hasPermission(deletePermission) ? "archive_tooltip" : undefined
              }
            >
              {t("Archive")}
            </PaddedDestructiveButton>
            <ReactTooltip id="archive_tooltip" effect="solid" />
            <PaddedPrimaryButton
              onClick={() => handleStatusChange("published")}
              loading={loadingState === "published"}
              disabled={
                isLoading ||
                !hasPermission(publishPermission) ||
                isDisabledBasedOnScoreThreshold
              }
              datatip={
                !hasPermission(publishPermission)
                  ? t("You do not have permission to publish this entity")
                  : publishButtonDisabledMessage
              }
              datafor={
                !hasPermission(publishPermission) ||
                isDisabledBasedOnScoreThreshold
                  ? "publish_tooltip"
                  : undefined
              }
            >
              {t("Publish")}
            </PaddedPrimaryButton>
          </>
        );
      case "archived":
        return (
          <>
            {statusIndicator}
            {shareButton}
            <PaddedPrimaryButton
              onClick={() => handleStatusChange("unpublished")}
              loading={loadingState === "unpublished"}
              disabled={isLoading || !hasPermission(publishPermission)}
              datatip={
                !hasPermission(publishPermission)
                  ? t("You do not have permission to restore this entity")
                  : undefined
              }
              datafor={
                !hasPermission(publishPermission)
                  ? "restore_tooltip"
                  : undefined
              }
            >
              {t("Restore")}
            </PaddedPrimaryButton>
          </>
        );
      default:
        return null;
    }
  };
  const collectionHasValues = (collection: CollectionSchema) => {
    return collectionHasDisplayableValues(
      collection,
      Boolean(metadata?.object_reference_enabled),
      canModify
    );
  };

  const groupHasValues = (group: AttributeGroupSummary) => {
    let hasValues = false;
    const shouldHideEmptyContent = !canModify;
    group?.attributes?.forEach((attribute) => {
      if (
        entityAttributeHasDisplayValue(attribute) &&
        ((!shouldHideEmptyContent && attribute.is_visible) ||
          (shouldHideEmptyContent &&
            attribute.is_visible &&
            !attribute.is_not_applicable))
      ) {
        hasValues = true;
      }
    });
    return hasValues;
  };

  const tabHasValues = (name: string) => {
    const shouldHideEmptyContent = !canModify;

    const tab = entity?.layout.find((tab) => tab.name === name);
    if (!tab?.items?.length) return false;

    // Fast return if any item should show the tab
    return tab.items.some((item) => {
      if (item.type === "collection") {
        const collection = entity?.collections.find(
          (c) => c.name.toLowerCase() === item.name.toLowerCase()
        );
        return !!(
          collection?.is_visible &&
          (!shouldHideEmptyContent || collectionHasValues(collection))
        );
      } else {
        const group = entity?.groups.find(
          (g) => g.name.toLowerCase() === item.name.toLowerCase()
        );
        const hasAttributes =
          !!group?.attributes?.length &&
          group?.attributes.some((attribute) => attribute.is_visible);
        const canShowGroup =
          (hasAttributes && groupHasValues(group)) ||
          (hasAttributes && !shouldHideEmptyContent);

        return canShowGroup;
      }
    });
  };

  if (!entity && !error) {
    return <DelayedSpinner />;
  }

  if (error || !entity) {
    return (
      <PageWrapper>
        <ErrorPlaceholder
          message={t("There was an error loading this entity.")}
        />
      </PageWrapper>
    );
  }

  const isRelatedEntitiesReady = getIsRelatedEntitiesReady({
    objectId: entity_id,
    objectReferenceEnabled: Boolean(metadata?.object_reference_enabled),
    relatedEntities,
    relatedEntitiesError,
  });

  if (!isRelatedEntitiesReady) {
    return <DelayedSpinner />;
  }

  // Build header configuration
  const showCompanyHierarchyTab =
    entity.schema?.enable_company_hierarchy &&
    !entity.layout?.some(
      (tab) =>
        tab.tab_type !== "company_hierarchy" &&
        tab.items.find((item) => item.type === "company_hierarchy")
    );

  const entityCoverImage = entity.cover_image?.signed_url;
  const layoutTabs = entity?.layout?.filter(
    (tab) =>
      tab.tab_type === "overview" ||
      (tab.tab_type === "documents" && showDocumentsTab) ||
      (tab.is_enabled && tabHasValues(tab.name)) ||
      (tab.tab_type === "company_hierarchy" && showCompanyHierarchyTab)
  );

  const headerConfig: DetailPageHeaderConfig = {
    breadcrumb: [
      {
        label: entityLabelPlural,
        path: listPath + (sessionStorage.getItem(`${entityType}_search`) ?? ""),
      },
      { label: entity.name, path: "" },
    ],
    title: entity.name,
    image: entityCoverImage
      ? {
          type: "url",
          url: entityCoverImage,
          alt: entity.name,
        }
      : {
          type: "icon",
          icon: CompanyIcon,
          size: 40,
        },
    metadata: buildEntityMetadata(
      entity,
      entityType,
      t,
      listPath,
      getTemplateDisplayName
    ),
    actions: statusActionButtons(),
    isParent: Boolean(entity.is_parent),
    isSubsidiary: Boolean(entity.is_subsidiary),
  };
  const showRelatedEntitiesTab = getShouldShowRelatedEntitiesTab({
    objectReferenceEnabled: Boolean(metadata?.object_reference_enabled),
    relatedEntities,
  });

  if (isLoadingDocumentAvailability) {
    return <DelayedSpinner />;
  }

  const showChangeRequestTab = metadata.enable_change_requests;

  const allTabs: TemplateTabData[] = [
    ...(layoutTabs ?? []),
    ...(showRelatedEntitiesTab
      ? [
          {
            id: "related_entities",
            display_name: t("Related Entities"),
            name: "Related Entities",
            tab_type: "custom" as const,
            is_enabled: true,
            priority: 998,
            items: [],
          },
        ]
      : []),
    ...(showChangeRequestTab
      ? [
          {
            id: "change_request_form",
            display_name: t("Change Requests"),
            name: "change_request_form",
            tab_type: "custom" as const,
            is_enabled: true,
            priority: 999,
            items: [],
          },
        ]
      : []),
    ...(canModify
      ? [
          {
            id: "activity",
            display_name: t("Activity"),
            name: "Activity",
            tab_type: "custom" as const,
            is_enabled: true,
            priority: 999,
            items: [],
          },
        ]
      : []),
  ];

  const changeRequestTabIndex = allTabs.findIndex(
    ({ id }) => id === "change_request_form"
  );
  changeRequestTabIndexRef.current = changeRequestTabIndex;

  return (
    <FullwidthPageWrapper>
      <DetailPageHeader config={headerConfig} />

      <ProfileDetailContentContainer
        pageContainerSettings={pageContainerSettings}
        pageLayout="single_column"
        surfaceBackground={surfaceBackground}
        isXlScreen={false}
        headerHeight={0}
      >
        <Tabs
          index={tabIndex}
          onChange={(nextIndex) => {
            if (
              changeRequestId &&
              tabIndex === changeRequestTabIndex &&
              nextIndex !== changeRequestTabIndex
            ) {
              setQuery({ changeRequestId: undefined, tabKey: undefined });
            }

            setTabIndex(nextIndex);
          }}
        >
          <TabList>
            {allTabs.map((tab) => (
              <Tab key={tab.id}>{tab.display_name ?? tab.name}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {allTabs.map((tab) => (
              <TabPanel key={tab.id}>
                {tab.id === "related_entities" && relatedEntities && (
                  <RelatedEntitiesTabContent
                    tenantIdOrSlug={tenant_id}
                    targetObjectType={entityType}
                    targetObjectId={entity_id}
                    relatedEntities={relatedEntities}
                    getSourceHref={(source) =>
                      source.object_type === "product"
                        ? `${adminPath}/pim/products/${source.id}`
                        : `${adminPath}/mdm/${source.object_type}s/${source.id}`
                    }
                  />
                )}

                {tab.id === "activity" && (
                  <ActivityTab
                    entity={entity}
                    entityType={entityType}
                    isTabActive={
                      tabIndex === allTabs.findIndex((t) => t.id === "activity")
                    }
                  />
                )}

                {tab.id !== "related_entities" &&
                  tab.id !== "activity" &&
                  tab.tab_type !== "change_request_form" &&
                  (tab.tab_type === "overview" ||
                    (tab.is_enabled &&
                      tab.tab_type === "custom" &&
                      tabHasValues(tab.name) &&
                      tab.items.length > 0) ||
                    canModify) &&
                  tab.tab_type !== "company_hierarchy" &&
                  tab.tab_type !== "documents" && (
                    <EntityTab
                      entity={entity}
                      mutate={mutate}
                      entityType={entityType}
                      tenantId={tenant_id}
                      tab={tab}
                    />
                  )}

                {showCompanyHierarchyTab &&
                  tab.tab_type === "company_hierarchy" &&
                  tab.is_enabled && (
                    <CompanyHierarchyTab
                      entity={entity}
                      entityType={entityType}
                      tenantId={tenant_id}
                      mutate={mutate}
                      tab={tab}
                    />
                  )}
                {tab.tab_type === "documents" && tab.is_enabled && (
                  <EntityDocuments
                    entity={entity}
                    entityType={entityType}
                    tenantId={tenant_id}
                    fetchEntityData={mutate}
                    isTabActive={
                      tabIndex ===
                      allTabs.findIndex((t) => t.tab_type === "documents")
                    }
                  />
                )}

                {tab.id === "change_request_form" && (
                  <ChangeRequestTab
                    entity={entity}
                    canModify={canModify}
                    isTabActive={
                      tabIndex ===
                      allTabs.findIndex((t) => t.id === "change_request_form")
                    }
                    entityType={entityType}
                  />
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </ProfileDetailContentContainer>
      {showArchiveWarningDialog && (
        <ObjectReferenceChangeConfirmDialog
          show={showArchiveWarningDialog}
          onClose={() => {
            setShowArchiveWarningDialog(false);
            handleArchiveOrDeleteDialogClose();
          }}
          onConfirm={async () => {
            setShowArchiveWarningDialog(false);
            if (pendingEntityAction === "deleted") {
              await handleDelete({ skipDeleteConfirmation: true });
            } else {
              await handleStatusChange("archived", {
                skipArchiveConfirmation: true,
              });
            }
            setPendingEntityAction(null);
          }}
          heading={
            pendingEntityAction === "deleted"
              ? t("Delete Profile?")
              : t("Archive Profile?")
          }
          message={getEntityArchiveOrDeleteWarningMessage(t, {
            pendingEntityAction,
            hasHierarchyImpact,
          })}
        />
      )}
      <ConfirmDialog
        show={showArchiveBasicConfirmDialog}
        closeDialog={() => {
          setShowArchiveBasicConfirmDialog(false);
          handleArchiveOrDeleteDialogClose();
        }}
        confirmMessage={t(
          `This entity will be ${pendingEntityAction}. Are you sure you want to continue?`
        )}
        handleConfirm={async () => {
          setShowArchiveBasicConfirmDialog(false);
          if (!pendingEntityAction) {
            return;
          }
          const action = pendingEntityAction;
          setPendingEntityAction(null);
          if (action === "deleted") {
            await handleDelete({ skipDeleteConfirmation: true });
          } else {
            await handleStatusChange("archived", {
              skipArchiveConfirmation: true,
            });
          }
        }}
        useV2Buttons={true}
      />
    </FullwidthPageWrapper>
  );
}
